/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useState, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Loader } from '@components/screen/Layout';
import Highcharts from '@components/chart/Highcharts';
import SociollaText from '@components/sociolla/SociollaText';
import SociollaCard from '@components/sociolla/SociollaCard';
import TitleSubSection from '@components/global/TitleSubSection';
import ModalFull from '@components/modal/ModalFull';
import ProgressChart from '@components/chart/ProgressChart';
import TextChart from '@components/chart/TextChart';
import SelectBox from '@components/form/SelectBox';
import CardHeaderCustom from '@/screens/home/components/snippets/CardHeaderCustom';
import BusinessChart from '@/screens/home/components/chart/BusinessChart';
import BusinessLinearChart from '@/screens/home/components/chart/BusinessLinearChart';
import BranchSearch from '@components/form/search/BranchSearch';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import SalesPersonSearch from '@components/form/search/SalesPersonSearch';
import CustomerClusterSearch from '@components/form/search/CustomerClusterSearch';
import ProductDashboard from '@/screens/home/components/snippets/ProductDashboard';
import MobileNationalChart from '@/screens/home/components/snippets/MobileNationalChart';
import MobileSalesByChart from '@/screens/home/components/snippets/MobileSalesByChart';
import MobileSalesBrandChart from '@/screens/home/components/snippets/MobileSalesBrandChart';
import CustomerPinpointDashboard from '@/screens/businessTracker/components/CustomerPinpointDashboard';

import { isWeb, isNative, fullWidth, fullHeight } from '@global/screens';
import {
  convertMonthNumber,
  deSlug,
  prettyNumber,
  convertTargetSettingValue,
  targetCalculation,
  hasDashboardAccess,
  changeTimeZone,
  getRandomColor,
  currency,
} from '@global/helpers';
import {
  FILTER_PERIOD,
  FILTER_YEARS,
  FILTER_CHANNELS,
  SALESMAN_TYPE,
  REGIONAL,
  MONTH_NAME_SIMPLE,
} from '@/constants';
import { MONTH_OBJ_NUMBERS, MONTH_KEYS } from '@/constants/target-settings';
import Colors from '@global/colors';

import { Dashboard } from '@api/dashboard';
import { MsAccount } from '@api/ms-account';
import { MsB2b } from '@api/ms-b2b';

const propTypes = {
  data: PropTypes.array,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onOpenProductFocus: PropTypes.func,
};

const defaultProps = {
  data: [],
  style: {},
  onOpenProductFocus: () => {},
};

const widthMobile = fullWidth - 30;
const widthLeft = isWeb ? 'calc(100% - 300px)' : widthMobile;
const widthRight = isWeb ? 280 : widthMobile;

const BRAND_SORT_BY = [
  { name: 'Highest - Lowest', value: '-target_value' },
  { name: 'Lowest - Highest', value: 'target_value' },
];

const ChartTab = ({
  isNormal = false,
  isBetween = false,
  tabStyle = {},
  tabs = [],
  activeTab = '',
  onChange = () => {},
}) => {
  return (
    <View
      style={[
        styles.tabWrapper,
        isNormal || !isWeb ? styles.tabWrapperNormal : styles.tabWrapperFull,
        !isWeb && isBetween ? styles.tabWrapperMobile : {},
        tabStyle,
      ]}>
      {tabs.map((item, idx) => (
        <TouchableOpacity
          activeOpacity={0.8}
          style={[
            styles.tabItem,
            activeTab == item.value ? styles.tabItemActive : {},
          ]}
          onPress={() => onChange(item.value)}
          key={idx}>
          <SociollaText
            style={{
              color: activeTab == item.value ? Colors.black : Colors.grey1,
            }}>
            {item.label}
          </SociollaText>
        </TouchableOpacity>
      ))}
    </View>
  );
};

const BusinessTracker = (props) => {
  const { onOpenProductFocus, onOpenSalesBrand } = props;
  const userData = useSelector((state) => state.auth.user);

  const today = changeTimeZone(new Date());
  const today_month = today.getMonth() + 1;
  const today_month_name = MONTH_NAME_SIMPLE[today.getMonth()];
  const today_period = `${convertMonthNumber(
    today_month,
  )}-${today.getFullYear()}`;

  const SALES_BY_TABS = useMemo(() => {
    return [
      { label: 'Branch', value: 'branch' },
      { label: 'Sales Team', value: 'salesteam' },
      { label: 'Channel', value: 'channel' },
      // { label: 'Cluster', value: 'cluster' },
    ].filter((a) => hasDashboardAccess(`sales-value-${a.value}`));
  }, [userData]);

  const AR_TABS = useMemo(() => {
    return [
      { label: 'National', value: 'national' },
      { label: 'Regional', value: 'regional' },
      { label: 'Branch', value: 'branch' },
      { label: 'Salesman', value: 'salesman' },
    ].filter((a) => hasDashboardAccess(`ar-${a.value}`));
  }, [userData]);

  const customSalesTeamFilter = () => {
    if (userData?.sales_team?.length) {
      return {
        _id: { $in: userData.sales_team.map((team) => team.id) },
      };
    } else {
      return {};
    }
  };

  // Product Focus
  const [loadingProductFocus, setLoadingProductFocus] = useState(false);
  const [dataProductFocus, setDataProductFocus] = useState({});
  const [filterSalesmanType, setFilterSalesmanType] = useState(
    SALESMAN_TYPE[0],
  );
  // Product Focus

  // DOORS National
  const [showTargetDoors, setShowTargetDoors] = useState(false);
  const [targetDoorType, setTargetDoorType] = useState('product');
  const [loadingDoors, setLoadingDoors] = useState(false);
  const [dataDoors, setDataDoors] = useState({});
  const [dataDoorsList, setDataDoorsList] = useState([]);
  // DOORS National

  // Account Receivable
  const [accountType, setAccountType] = useState('national');
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [accountFilter, setAccountFilter] = useState('');
  const [dataAccount, setDataAccount] = useState({
    chart: [],
    data: [],
  });
  // Account Receivable

  // National Sales Value
  const [showNationalChart, setShowNationalChart] = useState(false);
  const [nationalType, setNationalType] = useState('mtd');
  const [salesValueNationalFilter, setSalesValueNationalFilter] = useState(
    today.getFullYear(),
  );
  const [salesValueNationalFilterCompare, setSalesValueNationalFilterCompare] =
    useState({});
  const [loadingSalesValueNational, setLoadingSalesValueNational] =
    useState(false);
  const [dataSalesValueNationalToday, setDataSalesValueNationalToday] =
    useState({
      name: '',
      gross: 0,
      valid: 0,
      nett: 0,
      target: 0,
      percentage_gross: 0,
      percentage_valid: 0,
      percentage_nett: 0,
    });
  const [dataSalesValueNational, setDataSalesValueNational] = useState([]);
  // National Sales Value

  // Brand Sales Value
  const [showSalesBrandChart, setShowSalesBrandChart] = useState(false);
  const [salesBrandType, setSalesBrandType] = useState('mtd');
  const [salesValueBrandFilter, setSalesValueBrandFilter] = useState(
    FILTER_PERIOD.find((f) => f.value == today_period),
  );
  const [salesBrandSort, setSalesBrandSort] = useState(BRAND_SORT_BY[0]);
  const [loadingSalesValueBrand, setLoadingSalesValueBrand] = useState(false);
  const [dataSalesValueBrandToday, setDataSalesValueBrandToday] = useState({
    name: '',
    actual: 0,
    target: 0,
    percentage: 0,
  });
  const [dataSalesValueBrand, setDataSalesValueBrand] = useState([]);
  const [dataSalesValueBrandList, setDataSalesValueBrandList] = useState([]);
  const [dataBrandCategory, setDataBrandCategory] = useState([]);
  // Brand Sales Value

  // Sales By
  const [salesByType, setSalesByType] = useState('branch');
  const [showSalesByChart, setShowSalesByChart] = useState(false);
  const [salesByYearFilter, setSalesByYearFilter] = useState(
    today.getFullYear(),
  );
  const [salesByYearFilterCompare, setSalesByYearFilterCompare] = useState({});
  const [salesByFilter, setSalesByFilter] = useState('');
  const [salesByChannelFilter, setSalesByChannelFilter] = useState('');
  const [loadingSalesBy, setLoadingSalesBy] = useState(false);
  const [dataSalesByToday, setDataSalesByToday] = useState({
    name: '',
    gross: 0,
    valid: 0,
    nett: 0,
    target: 0,
    percentage_gross: 0,
    percentage_valid: 0,
    percentage_nett: 0,
  });
  const [dataSalesBy, setDataSalesBy] = useState([]);
  // Sales By

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (!accountType && AR_TABS?.length) {
        setAccountType(AR_TABS[0].value);
      }
    }
    return () => {
      mounted = false;
    };
  }, [AR_TABS]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (!salesByType && SALES_BY_TABS?.length) {
        setSalesByType(SALES_BY_TABS[0].value);
      }
    }
    return () => {
      mounted = false;
    };
  }, [SALES_BY_TABS]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (!showTargetDoors) {
        setTargetDoorType('product');
      }
    }
    return () => {
      mounted = false;
    };
  }, [showTargetDoors]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      getAccountReceivableDashboard();
    }
    return () => {
      mounted = false;
    };
  }, [accountType, accountFilter]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      getSalesValueDashboard();
    }
    return () => {
      mounted = false;
    };
  }, [nationalType, salesValueNationalFilter, salesValueNationalFilterCompare]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      getSalesByDashboard();
    }
    return () => {
      mounted = false;
    };
  }, [
    salesByType,
    salesByFilter,
    salesByYearFilter,
    salesByChannelFilter,
    salesByYearFilterCompare,
  ]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      getDoorDashboard();
    }
    return () => {
      mounted = false;
    };
  }, [showTargetDoors]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      getProductFocusDashboard();
    }
    return () => {
      mounted = false;
    };
  }, [filterSalesmanType]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      getSalesValueBrandDashboard();
    }
    return () => {
      mounted = false;
    };
  }, [salesBrandType, salesValueBrandFilter, salesBrandSort]);

  const getAccountReceivableDashboard = async () => {
    setLoadingAccount(true);
    try {
      const payload = {
        params: {
          filter: {},
        },
      };
      if (accountType == 'branch') {
        if (accountFilter?.id) {
          payload.params.filter['branch_id'] = accountFilter?.id;
        }
      } else if (accountType == 'regional') {
        if (accountFilter?.value) {
          payload.params.filter['region'] = accountFilter?.value;
        }
      } else if (accountType == 'salesman') {
        if (accountFilter?.id) {
          payload.params.filter['user_id'] = accountFilter?.id;
        }
      }
      const response = await Dashboard.getDashboardData(
        'account-receivable',
        accountType,
        payload,
      );
      if (response?.data?.data) {
        const result = {
          chart: [],
          data: [],
        };
        const res = response.data.data;
        const resArray = Object.keys(res);
        if (resArray?.length) {
          const nett = res['nett_order'];
          for (let i = 0; i < resArray.length; i++) {
            const key = resArray[i];
            if (key != 'nett_order') {
              result.color = [Colors.blue0, Colors.yellow0, Colors.green1];
              const category_name = deSlug(key.replace('_amount', ''));
              const percentage_value =
                nett && res[key] ? Math.round((res[key] / nett) * 100) : 0;
              result.data.push({
                label: category_name,
                from: prettyNumber(nett),
                to: `${percentage_value}%`,
                color: result.color[i],
              });
              result.chart.push({
                name: category_name,
                y: res[key],
                percentage_value: percentage_value,
              });
            }
          }
        }
        setDataAccount(result);
      } else {
        setDataAccount({
          chart: [],
          data: [],
        });
      }
      setLoadingAccount(false);
    } catch (err) {
      console.log('err', err);
      setDataAccount({
        chart: [],
        data: [],
      });
      setLoadingAccount(false);
    }
  };

  const getSalesValueDashboard = async () => {
    setLoadingSalesValueNational(true);
    try {
      const payload = {
        params: {
          filter: {
            year: salesValueNationalFilter?.value,
            is_mtd: nationalType == 'mtd',
            is_ytd: nationalType == 'ytd',
            compared_year: salesValueNationalFilterCompare?.value
              ? salesValueNationalFilterCompare.value
              : null,
          },
        },
      };
      const response = await Dashboard.getDashboardData(
        'sales-value',
        'national',
        payload,
      );
      let result = [];
      if (response?.data?.data?.length) {
        const dataLabels = {
          enabled: false,
          format: '{point.customPercentage:,f}%',
        };
        const temp = [
          {
            name: 'Gross',
            dataLabels,
            data: [],
            type: nationalType === 'ytd' ? 'line' : 'column',
            pointPlacement: -0.2,
          },
          {
            name: 'Valid',
            dataLabels,
            data: [],
            type: nationalType === 'ytd' ? 'line' : 'column',
            pointPlacement: -0.1,
          },
          {
            name: 'Nett',
            dataLabels,
            data: [],
            type: nationalType === 'ytd' ? 'line' : 'column',
          },
          {
            name: 'Target',
            dataLabels: { enabled: false },
            data: [],
            type: 'line',
          },
        ];
        if (salesValueNationalFilterCompare?.value) {
          temp.push({
            name: `Gross ${salesValueNationalFilterCompare.value}`,
            dataLabels,
            data: [],
          });
          temp.push({
            name: `Valid ${salesValueNationalFilterCompare.value}`,
            dataLabels,
            data: [],
          });
          temp.push({
            name: `Nett ${salesValueNationalFilterCompare.value}`,
            dataLabels: { enabled: false },
            data: [],
          });
        }
        const res = response.data.data.sort((a, b) => a.month - b.month);
        for (const month_key of MONTH_KEYS) {
          const month_number = MONTH_OBJ_NUMBERS[month_key];
          const item = res.find((r) => r.month == month_number);
          const gross = item?.gross_amount ? item.gross_amount : 0;
          const valid = item?.valid_amount ? item.valid_amount : 0;
          const nett = item?.nett_amount ? item.nett_amount : 0;
          const target = item?.target_value ? item.target_value : 0;
          const compared_gross = item?.compared_gross_amount
            ? item.compared_gross_amount
            : 0;
          const compared_valid = item?.compared_valid_amount
            ? item.compared_valid_amount
            : 0;
          const compared_nett = item?.compared_nett_amount
            ? item.compared_nett_amount
            : 0;
          let percentageGross = item?.percentage_gross
            ? item.percentage_gross
            : 0;
          if (gross > 0 && target > 0 && percentageGross == 0) {
            percentageGross = targetCalculation(gross, target);
          }
          let percentageValid = item?.percentage_valid
            ? item.percentage_valid
            : 0;
          if (valid > 0 && target > 0 && percentageValid == 0) {
            percentageValid = targetCalculation(valid, target);
          }
          let percentageNett = item?.percentage_nett ? item.percentage_nett : 0;
          if (nett > 0 && target > 0 && percentageNett == 0) {
            percentageNett = targetCalculation(nett, target);
          }
          if (month_number == today_month && item?.month) {
            setDataSalesValueNationalToday({
              name: MONTH_NAME_SIMPLE[item.month - 1]?.toUpperCase(),
              gross,
              valid,
              nett,
              target: target,
              percentage_gross: percentageGross,
              percentage_valid: percentageValid,
              percentage_nett: percentageNett,
            });
          }
          for (let j = 0; j < 4; j++) {
            let value = target;
            let percentage = 0;
            if (j == 0) {
              value = gross;
              percentage = percentageGross;
            } else if (j == 1) {
              value = valid;
              percentage = percentageValid;
            } else if (j == 2) {
              value = nett;
              percentage = percentageNett;
            }
            let percentage_color = Colors.blue0;
            if (j != 3 && value < target) {
              percentage_color = Colors.red1;
            }
            temp[j].data.push({
              y: value,
              ...(temp[j].name === 'Target'
                ? {}
                : {
                    customPercentage: `(${percentage} %)`,
                    color_percentage:
                      value < target ? Colors.red1 : Colors.blue0,
                  }),
              dataLabels: {
                color: percentage_color,
              },
            });
          }
          if (salesValueNationalFilterCompare?.value) {
            temp[4].data.push({
              y: compared_gross,
              dataLabels: { enabled: false },
            });
            temp[5].data.push({
              y: compared_valid,
              dataLabels: { enabled: false },
            });
            temp[6].data.push({
              y: compared_nett,
              dataLabels: { enabled: false },
            });
          }
        }
        result = temp;
      }
      setLoadingSalesValueNational(false);
      setDataSalesValueNational(result);
    } catch (error) {
      console.log('err-national', error);
      setLoadingSalesValueNational(false);
      setDataSalesValueNational([]);
    }
  };

  const getSalesByDashboard = async () => {
    setLoadingSalesBy(true);
    try {
      const payload = {
        params: {
          filter: {
            year: salesByYearFilter?.value,
            compared_year: salesByYearFilterCompare?.value
              ? salesByYearFilterCompare.value
              : null,
          },
        },
      };

      const filterId = salesByFilter?.id;
      const filterChannel = salesByChannelFilter?.value;

      if (salesByType == 'branch') {
        if (filterId) {
          payload.params.filter['branch_id'] = filterId;
        } else {
          // Set default branch
          const branches = await MsAccount.getBranches({
            params: {
              filter: { is_active: true },
              limit: 1,
            },
          });
          const branchesData = branches?.data?.data;
          if (branchesData?.length) {
            const userBranch = branchesData[0];
            setSalesByFilter({ ...userBranch, id: userBranch._id });
            payload.params.filter['branch_id'] = userBranch?._id;
          }
        }
      } else if (salesByType == 'cluster') {
        if (filterId) {
          payload.params.filter['cluster_id'] = filterId;
        }
      } else if (salesByType == 'salesteam') {
        if (filterId) {
          payload.params.filter['salesteam_id'] = filterId;
        } else {
          const salesTeamFilter = customSalesTeamFilter();
          const salesTeams = await MsB2b.getSalesTeam({
            params: {
              filter: salesTeamFilter,
              limit: 1,
            },
          });
          const salesTeamsData = salesTeams?.data?.data;
          if (salesTeamsData?.length) {
            const userSalesTeam = salesTeamsData[0];
            setSalesByFilter({ ...userSalesTeam, id: userSalesTeam._id });
            payload.params.filter['salesteam_id'] = userSalesTeam?._id;
          }
        }
      } else if (salesByType == 'channel') {
        if (filterChannel) {
          payload.params.filter['channel_id'] = filterChannel;
        } else {
          setSalesByChannelFilter({
            label: 'GT',
            value: 'gt',
          });
          payload.params.filter['channel_id'] = 'gt';
        }
      }

      if (!filterId && !filterChannel) {
        return;
      }

      const response = await Dashboard.getDashboardData(
        'sales-value',
        salesByType,
        payload,
      );
      let result = [];
      if (response?.data?.data?.length) {
        const dataLabels = {
          enabled: false,
          format: '{point.customPercentage:,f}%',
        };
        const temp = [
          {
            name: 'Gross',
            dataLabels,
            data: [],
            type: 'column',
            pointPlacement: -0.4,
          },
          {
            name: 'Valid',
            dataLabels,
            data: [],
            type: 'column',
            pointPlacement: -0.2,
          },
          {
            name: 'Nett',
            dataLabels,
            data: [],
            type: 'column',
          },
        ];
        if (salesByType != 'cluster') {
          temp.push({
            name: 'Target',
            dataLabels: { enabled: false },
            data: [],
          });
        }
        if (salesByYearFilterCompare?.value) {
          temp.push({
            name: `Gross ${salesByYearFilterCompare.value}`,
            dataLabels: { enabled: false },
            data: [],
            type: 'column',
            pointPlacement: -0.4,
          });
          temp.push({
            name: `Valid ${salesByYearFilterCompare.value}`,
            dataLabels: { enabled: false },
            data: [],
            type: 'column',
            pointPlacement: -0.2,
          });
          temp.push({
            name: `Nett ${salesByYearFilterCompare.value}`,
            dataLabels: { enabled: false },
            data: [],
            type: 'column',
          });
        }
        const res = response.data.data.sort((a, b) => a.month - b.month);
        for (const key_month of MONTH_KEYS) {
          const month_number = MONTH_OBJ_NUMBERS[key_month];
          const item = res.find((r) => r.month == month_number);
          const gross = item?.gross_amount ? item.gross_amount : 0;
          const valid = item?.valid_amount ? item.valid_amount : 0;
          const nett = item?.nett_amount ? item.nett_amount : 0;
          const target = item?.target_value ? item.target_value : 0;
          const compared_gross = item?.compared_gross_amount
            ? item.compared_gross_amount
            : 0;
          const compared_valid = item?.compared_valid_amount
            ? item.compared_valid_amount
            : 0;
          const compared_nett = item?.compared_nett_amount
            ? item.compared_nett_amount
            : 0;
          let percentageGross = item?.percentage_gross
            ? item.percentage_gross
            : 0;
          if (gross > 0 && target > 0 && percentageGross == 0) {
            percentageGross = targetCalculation(gross, target);
          }
          let percentageValid = item?.percentage_valid
            ? item.percentage_valid
            : 0;
          if (valid > 0 && target > 0 && percentageValid == 0) {
            percentageValid = targetCalculation(valid, target);
          }
          let percentageNett = item?.percentage_nett ? item.percentage_nett : 0;
          if (nett > 0 && target > 0 && percentageNett == 0) {
            percentageNett = targetCalculation(nett, target);
          }
          if (month_number == today_month && item?.month) {
            setDataSalesByToday({
              name: MONTH_NAME_SIMPLE[item.month - 1]?.toUpperCase(),
              gross,
              valid,
              nett,
              target: target,
              percentage_gross: percentageGross,
              percentage_valid: percentageValid,
              percentage_nett: percentageNett,
            });
          }
          const count = salesByType == 'cluster' ? 3 : 4;
          for (let j = 0; j < count; j++) {
            let value = target;
            let percentage = 0;
            if (j == 0) {
              value = gross;
              percentage = percentageGross;
            } else if (j == 1) {
              value = valid;
              percentage = percentageValid;
            } else if (j == 2) {
              value = nett;
              percentage = percentageNett;
            }
            let percentage_color = Colors.blue0;
            if (j != 3 && value < target) {
              percentage_color = Colors.red1;
            }
            temp[j].data.push({
              y: value,
              ...(temp[j].name === 'Target'
                ? {}
                : {
                    customPercentage: `(${percentage} %)`,
                    color_percentage:
                      value < target ? Colors.blue0 : Colors.red1,
                  }),
              dataLabels: {
                color: percentage_color,
              },
            });
          }
          if (salesByYearFilterCompare?.value) {
            temp[count].data.push({
              y: compared_gross,
              dataLabels: { enabled: false },
            });
            temp[count + 1].data.push({
              y: compared_valid,
              dataLabels: { enabled: false },
            });
            temp[count + 2].data.push({
              y: compared_nett,
              dataLabels: { enabled: false },
            });
          }
        }
        result = temp;
      }
      setDataSalesBy(result);
      setLoadingSalesBy(false);
    } catch (err) {
      console.log('err-national', err);
      setDataSalesBy([]);
      setLoadingSalesBy(false);
    }
  };

  const getDoorDashboard = async () => {
    setLoadingDoors(true);
    try {
      const payload = {
        params: {
          filter: {},
        },
      };
      if (showTargetDoors) {
        payload.params.filter.see_more = true;
      }
      const response = await Dashboard.getDashboardData(
        'doors',
        'national',
        payload,
      );
      if (response?.data?.data) {
        const res = response.data.data;
        if (showTargetDoors) {
          if (res?.length) {
            setDataDoorsList(res);
          } else {
            setDataDoorsList([]);
          }
        } else {
          setDataDoors(res);
        }
      } else {
        if (showTargetDoors) {
          setDataDoorsList([]);
        } else {
          setDataDoors({});
        }
      }
      setLoadingDoors(false);
    } catch (err) {
      console.log('err', err);
      if (showTargetDoors) {
        setDataDoorsList([]);
      } else {
        setDataDoors({});
      }
      setLoadingDoors(false);
    }
  };

  const getProductFocusDashboard = async () => {
    setLoadingProductFocus(true);
    try {
      const payload = {
        params: {
          filter: {
            year: today.getFullYear(),
            month: today_month,
            salesman_type: filterSalesmanType?.value,
          },
        },
      };
      const response = await Dashboard.getDashboardData(
        'product-focus',
        'salesman',
        payload,
      );
      if (response?.data?.data) {
        const res = response.data.data;
        setDataProductFocus(res);
      } else {
        setDataProductFocus({});
      }
      setLoadingProductFocus(false);
    } catch (err) {
      console.log('err', err);
      setDataProductFocus({});
      setLoadingProductFocus(false);
    }
  };

  const getSalesValueBrandDashboard = async () => {
    setLoadingSalesValueBrand(true);
    try {
      const payload = {
        params: {
          filter: {
            month: salesValueBrandFilter?.month,
            is_mtd: salesBrandType == 'mtd',
            is_ytd: salesBrandType == 'ytd',
          },
          sort: salesBrandSort?.value,
        },
      };
      const response = await Dashboard.getDashboardData(
        'sales-value',
        'brand',
        payload,
      );
      let result = [];
      let lists = [];
      if (response?.data?.data?.length) {
        lists = response.data.data;
        let temp = [
          {
            name: 'Actual',
            dataLabels: { enabled: true },
            data: [],
            percentage: [],
          },
          {
            name: 'Target',
            dataLabels: { enabled: false },
            data: [],
            percentage: [],
          },
        ];
        if (salesBrandType == 'ytd') {
          temp = [];
          for (let i = 0; i < lists.length; i++) {
            const item = lists[i];
            const keys = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec',
            ];
            temp.push({
              name: item?.brand?.name,
              dataLabels: { enabled: false },
              data: [],
            });
            for (let j = 0; j < keys.length; j++) {
              const key = keys[j];
              temp[i].data.push({
                y: item[key],
                color: getRandomColor(),
              });
            }
          }
        } else {
          const res = response.data.data.sort((a, b) => a.month - b.month);
          for (let i = 0; i < MONTH_KEYS.length; i++) {
            const month_key = MONTH_KEYS[i];
            const month_number = MONTH_OBJ_NUMBERS[month_key];
            const item = res.find((r) => r.month == month_number);
            const actual = item?.actual_value ? item.actual_value : 0;
            const target = item?.target_value ? item.target_value : 0;
            let percentage = item?.percentage ? item.percentage : 0;
            if (actual > 0 && target > 0 && percentage == 0) {
              percentage = targetCalculation(actual, target);
            }
            if (month_number == today_month && item?.month) {
              setDataSalesValueBrandToday({
                name: month_key?.toUpperCase(),
                actual: actual,
                target: target,
                percentage: percentage,
              });
            }
            for (let j = 0; j < 2; j++) {
              const value = j == 0 ? actual : target;
              let percentage_color = Colors.green1;
              if (j == 0 && actual < target) {
                percentage_color = Colors.red0;
              }
              temp[j].data.push({
                y: value,
                percentage: percentage,
                dataLabels: {
                  color: percentage_color,
                  format: '{point.percentage}%',
                },
              });
              if (j == 0) {
                temp[j].percentage.push(percentage);
              }
            }
          }
        }
        result = temp;
      }
      setLoadingSalesValueBrand(false);
      setDataSalesValueBrand(result);
      setDataSalesValueBrandList(lists);
      const categories = [];
      for (let i = 0; i < lists.length; i++) {
        const item = lists[i];
        if (categories.findIndex((c) => c.label == item?.brand?.name) == -1) {
          categories.push({
            label: item?.brand?.name,
            color: Colors.blue0,
            value: item?.actual_value || 0,
          });
        }
      }
      setDataBrandCategory(categories);
    } catch (error) {
      console.log('err-national', error);
      setLoadingSalesValueBrand(false);
      setDataSalesValueBrand([]);
      setDataSalesValueBrandList([]);
      setDataBrandCategory([]);
    }
  };

  return (
    <View
      style={[
        styles.fullWidth,
        styles.rowBetween,
        styles.rowWrap,
        !isWeb ? styles.rowColumn : {},
      ]}>
      <View style={styles.leftSide}>
        {/* Customer Pinpoint Start */}
        <CustomerPinpointDashboard />
        {/* Customer Pinpoint End */}

        {/* National Sales Start */}
        {hasDashboardAccess('sales-value-national') ? (
          <>
            <SociollaCard
              titleStyle={styles.titleStyle}
              style={styles.fullWidth}
              header={
                <CardHeaderCustom
                  title="National Sales"
                  textRight={isWeb ? null : 'see details'}
                  onPress={() => (isWeb ? null : setShowNationalChart(true))}
                />
              }
              onPress={() => null}>
              <ChartTab
                tabs={[
                  { label: 'MTD', value: 'mtd' },
                  { label: 'YTD', value: 'ytd' },
                ]}
                activeTab={nationalType}
                onChange={(value) => setNationalType(value)}
              />
              {isWeb ? (
                <View style={styles.filterChartWrapper}>
                  <View style={styles.filterChartItem}>
                    <SociollaText style={styles.filterNet}>
                      Sales Year
                    </SociollaText>
                    <SelectBox
                      label="Sales Year"
                      hideLabel
                      options={FILTER_YEARS}
                      value={salesValueNationalFilter}
                      placeholder="Select Sales Year"
                      trackBy="value"
                      labelKey="label"
                      onChange={(value) => {
                        setSalesValueNationalFilter(value);
                      }}
                    />
                  </View>
                  <View style={styles.filterChartItem}>
                    <SociollaText style={styles.filterNet}>
                      Compare with
                    </SociollaText>
                    <SelectBox
                      label="Compare with"
                      hideLabel
                      options={FILTER_YEARS}
                      value={salesValueNationalFilterCompare}
                      placeholder="Select Compare with"
                      trackBy="value"
                      labelKey="label"
                      onChange={(value) => {
                        setSalesValueNationalFilterCompare(value);
                      }}
                    />
                  </View>
                </View>
              ) : (
                false
              )}
              {loadingSalesValueNational ? (
                <Loader />
              ) : dataSalesValueNational?.length ? (
                <View style={styles.chartWrapper}>
                  <BusinessChart
                    data={dataSalesValueNational}
                    line={nationalType == 'ytd'}
                    colors={[
                      Colors.green0,
                      Colors.yellow0,
                      Colors.blue4,
                      Colors.purple0,
                    ]}
                    // columnStackingOption={'normal'}
                    tooltipYFormat={`{point.y:,.0f} <span style="color:{point.color_percentage}">{point.customPercentage:,f}</span`}
                  />
                </View>
              ) : (
                <View style={styles.noData}>
                  <SociollaText>
                    No data found. Please use another filter!
                  </SociollaText>
                </View>
              )}
            </SociollaCard>
            <MobileNationalChart
              show={showNationalChart}
              data={dataSalesValueNational}
              dataMonth={dataSalesValueNationalToday}
              loading={loadingSalesValueNational}
              type={nationalType}
              filter={salesValueNationalFilter}
              onChangeTab={setNationalType}
              onChangeFilter={setSalesValueNationalFilter}
              onClose={() => setShowNationalChart(false)}
            />
          </>
        ) : (
          false
        )}
        {/* National Sales End */}

        {/* Sales By Start */}
        {SALES_BY_TABS?.length ? (
          <>
            <SociollaCard
              titleStyle={styles.titleStyle}
              style={styles.fullWidth}
              header={
                <CardHeaderCustom
                  title="Sales By"
                  textRight={isWeb ? null : 'see details'}
                  onPress={() => (isWeb ? null : setShowSalesByChart(true))}
                />
              }
              onPress={() => null}>
              <ChartTab
                isBetween
                tabs={SALES_BY_TABS}
                activeTab={salesByType}
                onChange={(value) => {
                  setSalesByFilter('');
                  setSalesByType(value);
                }}
              />
              <View style={styles.filterChartWrapper}>
                {salesByType == 'branch' ? (
                  <View style={!isWeb ? styles.branchFilterMobile : {}}>
                    <BranchSearch
                      value={salesByFilter}
                      hideLabel={!isWeb}
                      arrayOne
                      customFilter={{ is_active: true }}
                      onChange={(value) => {
                        setSalesByFilter(value);
                      }}
                    />
                  </View>
                ) : salesByType == 'salesteam' ? (
                  <View style={!isWeb ? styles.branchFilterMobile : {}}>
                    <SalesTeamSearch
                      value={salesByFilter}
                      hideLabel={!isWeb}
                      onChange={(value) => {
                        setSalesByFilter(value);
                      }}
                      customFilter={customSalesTeamFilter()}
                    />
                  </View>
                ) : salesByType == 'cluster' ? (
                  <View style={!isWeb ? styles.branchFilterMobile : {}}>
                    <CustomerClusterSearch
                      label="Cluster"
                      value={salesByFilter}
                      onChange={(value) => {
                        setSalesByFilter(value);
                      }}
                    />
                  </View>
                ) : salesByType == 'channel' ? (
                  <SelectBox
                    label="Channel"
                    options={FILTER_CHANNELS}
                    value={salesByChannelFilter}
                    placeholder="Select Channel"
                    trackBy="value"
                    labelKey="label"
                    onChange={(value) => {
                      setSalesByChannelFilter(value);
                    }}
                  />
                ) : (
                  false
                )}
                {isWeb ? (
                  <>
                    <SelectBox
                      label="Sales Year"
                      options={FILTER_YEARS}
                      value={salesByYearFilter}
                      placeholder="Select Sales Year"
                      trackBy="value"
                      labelKey="label"
                      onChange={(value) => {
                        setSalesByYearFilter(value);
                      }}
                    />
                    <SelectBox
                      label="Compare with"
                      options={FILTER_YEARS}
                      value={salesByYearFilterCompare}
                      placeholder="Select Compare with"
                      trackBy="value"
                      labelKey="label"
                      onChange={(value) => {
                        setSalesByYearFilterCompare(value);
                      }}
                    />
                  </>
                ) : (
                  false
                )}
              </View>
              {loadingSalesBy ? (
                <Loader />
              ) : dataSalesBy?.length ? (
                <View style={styles.chartWrapper}>
                  <BusinessChart
                    data={dataSalesBy}
                    colors={[
                      Colors.green0,
                      Colors.yellow0,
                      Colors.blue4,
                      Colors.purple0,
                    ]}
                    // columnStackingOption={'normal'}
                    tooltipYFormat={`{point.y:,.0f} <span style="color:{point.color_percentage}">{point.customPercentage:,f}</span`}
                  />
                </View>
              ) : (
                <View style={styles.noData}>
                  <SociollaText>
                    No data found. Please use another filter!
                  </SociollaText>
                </View>
              )}
            </SociollaCard>
            <MobileSalesByChart
              show={showSalesByChart}
              data={dataSalesBy}
              type={salesByType}
              tabs={SALES_BY_TABS}
              onChangeType={setSalesByType}
              dataMonth={dataSalesByToday}
              loading={loadingSalesBy}
              filter={salesByFilter}
              filterYear={salesByYearFilter}
              filterChannel={salesByChannelFilter}
              onChangeFilter={setSalesByFilter}
              onChangeYear={setSalesByYearFilter}
              onChangeChannel={setSalesByChannelFilter}
              onClose={() => setShowSalesByChart(false)}
            />
          </>
        ) : (
          false
        )}
        {/* Sales By End */}

        {/* Sales Brand Start */}
        {hasDashboardAccess('sales-value-brand') ? (
          <>
            <SociollaCard
              titleStyle={styles.titleStyle}
              style={styles.fullWidth}
              header={
                <CardHeaderCustom
                  title="Sales Brand"
                  textRight={
                    salesBrandType == 'mtd'
                      ? isWeb
                        ? 'see more'
                        : 'see details'
                      : null
                  }
                  onPress={() => {
                    if (salesBrandType == 'mtd') {
                      if (isWeb) {
                        onOpenSalesBrand();
                      } else {
                        setShowSalesBrandChart(true);
                      }
                    }
                  }}
                />
              }
              onPress={() => null}>
              <ChartTab
                tabs={[
                  { label: 'MTD', value: 'mtd' },
                  { label: 'YTD', value: 'ytd' },
                ]}
                tabStyle={styles.tabChart}
                activeTab={salesBrandType}
                onChange={(value) => setSalesBrandType(value)}
              />
              {isWeb && salesBrandType == 'mtd' ? (
                <View style={styles.filterChartWrapper}>
                  <View style={styles.filterChartItem}>
                    <SociollaText style={styles.filterNet}>Filter</SociollaText>
                    <SelectBox
                      label="Filter"
                      hideLabel
                      options={FILTER_PERIOD}
                      value={salesValueBrandFilter}
                      placeholder="Select Filter"
                      trackBy="value"
                      labelKey="label"
                      onChange={(value) => {
                        setSalesValueBrandFilter(value);
                      }}
                    />
                  </View>
                  <View style={styles.filterChartItem}>
                    <SociollaText style={styles.filterNet}>
                      Sort By
                    </SociollaText>
                    <SelectBox
                      label="Sort By"
                      hideLabel
                      options={BRAND_SORT_BY}
                      value={salesBrandSort}
                      placeholder="Select Sort By"
                      trackBy="value"
                      labelKey="name"
                      onChange={(value) => {
                        setSalesBrandSort(value);
                      }}
                    />
                  </View>
                </View>
              ) : (
                false
              )}
              <View
                style={[
                  styles.chartWrapper,
                  salesBrandType == 'mtd' ? styles.chartWrapperNormal : {},
                ]}>
                {loadingSalesValueBrand ? (
                  <Loader />
                ) : dataSalesValueBrand?.length ? (
                  <>
                    {salesBrandType == 'ytd' ? (
                      <BusinessChart data={dataSalesValueBrand} line />
                    ) : (
                      <BusinessLinearChart
                        data={dataSalesValueBrandList}
                        styleCategory={styles.categoryStyle}
                        target={
                          dataSalesValueBrandList?.[
                            salesBrandSort?.value == '-target_value'
                              ? 0
                              : dataSalesValueBrandList.length - 1
                          ]?.target_value || 0
                        }
                        categories={[
                          {
                            label: 'Actual',
                            color: Colors.blue0,
                            value: '40K',
                          },
                          {
                            label: 'Target',
                            color: Colors.yellow0,
                            value: '60K',
                          },
                        ]}
                      />
                    )}
                  </>
                ) : (
                  <View style={styles.noData}>
                    <SociollaText>
                      No data found. Please use another filter!
                    </SociollaText>
                  </View>
                )}
              </View>
            </SociollaCard>
            <MobileSalesBrandChart
              show={showSalesBrandChart}
              data={dataSalesValueBrand}
              dataTwo={dataSalesValueBrandList}
              todayData={dataSalesValueBrandToday}
              loading={loadingSalesValueBrand}
              type={salesBrandType}
              sort={salesBrandSort}
              filterPeriod={salesValueBrandFilter}
              categories={
                salesBrandType == 'ytd'
                  ? dataBrandCategory
                  : [
                      { label: 'Actual', color: Colors.blue0, key: 'actual' },
                      { label: 'Target', color: Colors.yellow0, key: 'target' },
                    ]
              }
              onChangeType={setSalesBrandType}
              onSort={setSalesBrandSort}
              onFilter={setSalesValueBrandFilter}
              onClose={() => setShowSalesBrandChart(false)}
            />
          </>
        ) : (
          false
        )}
        {/* Sales Brand End */}

        {/* Account Receivable Start */}
        {AR_TABS?.length ? (
          <SociollaCard
            titleStyle={styles.titleStyle}
            style={styles.fullWidth}
            header={<CardHeaderCustom title="Account Receivable" />}
            onPress={() => null}>
            <View style={[styles.rowBetween, !isWeb ? styles.filterPie : {}]}>
              <ChartTab
                isNormal
                isBetween
                tabs={AR_TABS}
                activeTab={accountType}
                onChange={(value) => {
                  setAccountFilter('');
                  setAccountType(value);
                }}
              />
              <View style={!isWeb ? styles.filterPieBox : {}}>
                {accountType == 'branch' ? (
                  <BranchSearch
                    value={accountFilter}
                    hideLabel
                    arrayOne
                    onChange={(value) => {
                      setAccountFilter(value);
                    }}
                  />
                ) : accountType == 'salesman' ? (
                  <SalesPersonSearch
                    value={accountFilter}
                    hideLabel
                    isGroup
                    onChange={(value) => {
                      setAccountFilter(value);
                    }}
                  />
                ) : accountType == 'regional' ? (
                  <SelectBox
                    label="Regional"
                    hideLabel
                    options={REGIONAL}
                    value={accountFilter}
                    placeholder="Select Filter"
                    trackBy="value"
                    labelKey="name"
                    onChange={(value) => {
                      setAccountFilter(value);
                    }}
                  />
                ) : (
                  false
                )}
              </View>
            </View>
            <View style={[styles.rowBetween, !isWeb ? styles.pieMobile : {}]}>
              {dataAccount?.chart?.length ? (
                <>
                  <View style={styles.pieChartWrapper}>
                    <Highcharts
                      type="pie"
                      height={!isNative ? 430 : 200}
                      data={[{ name: '', data: dataAccount.chart }]}
                      categories={dataAccount.data.map((item) => item.label)}
                      colors={dataAccount.data.map((item) => item.color)}
                    />
                  </View>
                  <View style={styles.pieCat}>
                    {dataAccount.data.map((item, idx) => (
                      <View
                        style={[styles.pieCatItem, styles.minBottom]}
                        key={idx}>
                        <View style={styles.labelCatChild} key={idx}>
                          <View
                            style={[
                              styles.labelCatBox,
                              { backgroundColor: item.color },
                            ]}
                          />
                          <SociollaText style={styles.labelCatText}>
                            {item.label}
                          </SociollaText>
                        </View>
                        <View
                          style={[
                            styles.labelCatChild,
                            !isWeb ? styles.labelCatChild2 : {},
                          ]}>
                          <SociollaText
                            type="medium"
                            style={[
                              styles.labelCatBigText,
                              styles.minRight,
                              { color: item.color },
                            ]}>
                            {item.to}
                          </SociollaText>
                          <SociollaText
                            style={[
                              styles.labelCatBigText,
                              !isWeb ? styles.labelCatBigText2 : {},
                              { color: Colors.grey1 },
                            ]}>
                            ~ {item.from}
                          </SociollaText>
                        </View>
                      </View>
                    ))}
                  </View>
                </>
              ) : loadingAccount ? (
                <Loader />
              ) : (
                <View style={styles.noData}>
                  <SociollaText>
                    No data found. Please use another filter!
                  </SociollaText>
                </View>
              )}
            </View>
          </SociollaCard>
        ) : (
          false
        )}
        {/* Account Receivable End */}
      </View>
      <View style={styles.rightSide}>
        {/* Product Focus Start */}
        {hasDashboardAccess('product-focus-national') ? (
          <SociollaCard
            titleStyle={styles.titleStyle}
            style={styles.fullWidth}
            header={
              <CardHeaderCustom
                title="Product Focus"
                textRight="see more"
                onPress={() => onOpenProductFocus()}
              />
            }
            onPress={() => null}>
            {loadingProductFocus ? (
              <Loader />
            ) : (
              <>
                <View
                  style={[
                    styles.filterNetContainer,
                    isWeb ? styles.filterNetContainerSalesmanType : {},
                  ]}>
                  <SociollaText
                    style={[styles.filterNet, styles.filterNetSalesmanType]}>
                    Salesman Type
                  </SociollaText>
                  <View style={styles.filterNetBoxSalesmanType}>
                    <SelectBox
                      label="Salesman Type"
                      hideLabel
                      options={SALESMAN_TYPE}
                      value={filterSalesmanType}
                      placeholder="Select Salesman Type"
                      trackBy="value"
                      labelKey="label"
                      isDropdown={isWeb}
                      onChange={(value) => {
                        setFilterSalesmanType(value);
                      }}
                    />
                  </View>
                </View>
                <ProgressChart
                  title={dataProductFocus?.product_focus1?.brand?.name || '-'}
                  target={convertTargetSettingValue(
                    dataProductFocus?.product_focus1?.target,
                    dataProductFocus?.product_focus1?.target_value_type,
                  )}
                  count={targetCalculation(
                    dataProductFocus?.product_focus1?.actual,
                    dataProductFocus?.product_focus1?.target,
                  )}
                  popupText={`${today_month_name}: ${currency(
                    dataProductFocus?.product_focus1?.actual || 0,
                  )}`}
                  showPopup
                  bordered
                  titleRowMobile
                  leftSpace
                />
                <ProgressChart
                  title={dataProductFocus?.product_focus2?.brand?.name || '-'}
                  target={convertTargetSettingValue(
                    dataProductFocus?.product_focus2?.target,
                    dataProductFocus?.product_focus2?.target_value_type,
                  )}
                  activeColor={Colors.yellow0}
                  count={targetCalculation(
                    dataProductFocus?.product_focus2?.actual,
                    dataProductFocus?.product_focus2?.target,
                  )}
                  popupText={`${today_month_name}: ${currency(
                    dataProductFocus?.product_focus2?.actual || 0,
                  )}`}
                  showPopup
                  bordered
                  titleRowMobile
                  leftSpace
                />
                <ProgressChart
                  title={dataProductFocus?.product_focus3?.brand?.name || '-'}
                  target={convertTargetSettingValue(
                    dataProductFocus?.product_focus3?.target,
                    dataProductFocus?.product_focus3?.target_value_type,
                  )}
                  activeColor={Colors.blue4}
                  count={targetCalculation(
                    dataProductFocus?.product_focus3?.actual,
                    dataProductFocus?.product_focus3?.target,
                  )}
                  popupText={`${today_month_name}: ${currency(
                    dataProductFocus?.product_focus3?.actual || 0,
                  )}`}
                  showPopup
                  titleRowMobile
                  leftSpace
                />
              </>
            )}
          </SociollaCard>
        ) : (
          false
        )}
        {/* Product Focus End */}

        {/* Total DOORS This Month Start */}
        {hasDashboardAccess('doors-national') ? (
          <SociollaCard
            titleStyle={styles.titleStyle}
            style={styles.fullWidth}
            header={
              <CardHeaderCustom
                title="Total DOORS this month"
                textRight="see target"
                onPress={() => setShowTargetDoors(true)}
              />
            }
            onPress={() => null}>
            {loadingDoors ? (
              <Loader />
            ) : (
              <>
                <TextChart
                  title="New Customer"
                  total={dataDoors?.new_customers || 0}
                  activeColor={Colors.yellow0}
                  bordered
                />
                <TextChart
                  title="Existing Customer"
                  total={dataDoors?.existing_customers || 0}
                  activeColor={Colors.blue4}
                  bordered
                />
                <ProgressChart
                  title="Target Customer"
                  target={convertTargetSettingValue(
                    dataDoors?.target_value || 0,
                    'doors',
                  )}
                  count={dataDoors?.actual_value || 0}
                  popupText={`${today_month_name}: ${currency(
                    dataDoors.actual_value || 0,
                  )}`}
                  showPopup
                  titleRowMobile
                  leftSpace
                />
              </>
            )}
          </SociollaCard>
        ) : (
          false
        )}
        {/* Total DOORS This Month End */}

        {/* Order Start */}
        {/* <SociollaCard
          titleStyle={styles.titleStyle}
          style={styles.fullWidth}
          header={
            <View style={styles.filterNetContainer}>
              <SociollaText
                style={[styles.filterNet, !isWeb ? styles.filterNetOrder : {}]}>
                Filter
              </SociollaText>
              <View style={!isWeb ? styles.filterNetBox : {}}>
                <SelectBox
                  label="Filter"
                  hideLabel
                  options={[{ name: 'YTD', value: 'ytd' }]}
                  value={{ name: 'YTD', value: 'ytd' }}
                  placeholder="Select Filter"
                  trackBy="value"
                  labelKey="name"
                  selectContainer={{ width: isWeb ? 205 : '100%' }}
                  onChange={(value) => {
                    console.log('value', value);
                  }}
                />
              </View>
            </View>
          }
          onPress={() => null}>
          <TextChart
            title="Nett Order"
            titleBold
            totalComponent={
              <View>
                <View style={styles.netOrderTotal}>
                  <SociollaText type="bold" style={styles.netOrderText}>
                    Rp 6,7M
                  </SociollaText>
                  <View>
                    <SociollaText type="medium" style={styles.netOrderRightTop}>
                      vs Last Year
                    </SociollaText>
                    <SociollaText
                      type="medium"
                      style={styles.netOrderRightBottom}>
                      ^ 1,3 M
                    </SociollaText>
                  </View>
                </View>
                <ProgressChart
                  title="YTD"
                  titleStyle={styles.ytdText}
                  containerStyle={styles.progressNetStyle}
                  count={75}
                />
              </View>
            }
            activeColor={Colors.yellow0}
            bordered
          />
          <TextChart
            title="Gross Order"
            titleBold
            desc="YTD"
            total="Rp 12M"
            activeColor={Colors.yellow0}
            bordered
          />
          <TextChart
            title="Valid Order"
            titleBold
            desc="Total order book"
            total="Rp 1,2M"
            activeColor={Colors.blue4}
            bordered
          />
          <TextChart title="Service Level" titleBold total="85%" />
        </SociollaCard> */}
        {/* Order End */}
      </View>
      <ModalFull
        show={showTargetDoors}
        title="Target DOORS"
        onClose={() => setShowTargetDoors(false)}>
        <ScrollView
          style={[
            styles.fullWidthNormal,
            { maxHeight: fullHeight - (isWeb ? 110 : 55) },
          ]}>
          <View style={styles.tabDoors}>
            <TouchableOpacity
              activeOpacity={0.8}
              style={[
                styles.tabDoorsItem,
                targetDoorType == 'product' ? styles.tabDoorsItemActive : {},
              ]}
              onPress={() => setTargetDoorType('product')}>
              <SociollaText
                type={targetDoorType == 'product' ? 'medium' : 'regular'}
                style={[
                  styles.tabDoorsText,
                  targetDoorType == 'product' ? styles.tabDoorsTextActive : {},
                ]}>
                Product
              </SociollaText>
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.8}
              style={[
                styles.tabDoorsItem,
                targetDoorType == 'brand' ? styles.tabDoorsItemActive : {},
              ]}
              onPress={() => setTargetDoorType('brand')}>
              <SociollaText
                type={targetDoorType == 'brand' ? 'medium' : 'regular'}
                style={[
                  styles.tabDoorsText,
                  targetDoorType == 'brand' ? styles.tabDoorsTextActive : {},
                ]}>
                Brand
              </SociollaText>
            </TouchableOpacity>
          </View>
          {loadingDoors ? (
            <Loader />
          ) : dataDoorsList?.length ? (
            <>
              {targetDoorType == 'product' ? (
                dataDoorsList.map((item, idx) => (
                  <View key={idx}>
                    <TitleSubSection title={item?.brand?.name || '-'} />
                    <View
                      style={[
                        styles.fullWidth,
                        !isWeb ? styles.fullWidthMobile : {},
                      ]}>
                      {(item?.products || []).map((child, childIdx) => (
                        // <ProductDashboard key={idx} />
                        <View style={styles.productWrapper} key={childIdx}>
                          <ProgressChart
                            titleComponent={
                              <ProductDashboard
                                product={child}
                                style={styles.productItem}
                              />
                            }
                            titleRowMobile={!isWeb}
                            titleRowEndMobile={!isWeb}
                            target={convertTargetSettingValue(
                              child.target_value || 0,
                              child?.target_type || 'amount',
                            )}
                            targetStyle={styles.titleProgressBar}
                            containerStyle={styles.progressProduct}
                            count={child.percentage || 0}
                            popupText={`${today_month_name}: ${convertTargetSettingValue(
                              child.actual_value || 0,
                              child?.target_type || 'amount',
                              false,
                            )}`}
                            showPopup
                            leftSpace
                          />
                        </View>
                      ))}
                    </View>
                  </View>
                  // <>
                  //   <TitleSubSection title={item} />
                  //   <View
                  //     style={[
                  //       styles.fullWidth,
                  //       !isWeb ? styles.fullWidthMobile : {},
                  //     ]}>
                  //     {[1, 2, 3].map((item, idx) => (
                  //       // <ProductDashboard key={idx} />
                  //       <View style={styles.productWrapper} key={idx}>
                  //         <ProgressChart
                  //           titleComponent={
                  //             <ProductDashboard style={styles.productItem} />
                  //           }
                  //           titleRowMobile={!isWeb}
                  //           titleRowEndMobile={!isWeb}
                  //           target="Rp 12M"
                  //           targetStyle={styles.titleProgressBar}
                  //           containerStyle={styles.progressProduct}
                  //           count={40}
                  //         />
                  //       </View>
                  //     ))}
                  //   </View>
                  //   <TitleSubSection title="Sukin" />
                  //   <View
                  //     style={[
                  //       styles.fullWidth,
                  //       !isWeb ? styles.fullWidthMobile : {},
                  //     ]}>
                  //     {[1, 2, 3].map((item, idx) => (
                  //       // <ProductDashboard key={idx} />
                  //       <View style={styles.productWrapper} key={idx}>
                  //         <ProgressChart
                  //           titleComponent={
                  //             <ProductDashboard style={styles.productItem} />
                  //           }
                  //           titleRowMobile={!isWeb}
                  //           titleRowEndMobile={!isWeb}
                  //           target="Rp 12M"
                  //           targetStyle={styles.titleProgressBar}
                  //           containerStyle={styles.progressProduct}
                  //           count={40}
                  //         />
                  //       </View>
                  //     ))}
                  //   </View>
                  // </>
                ))
              ) : (
                <View
                  style={[
                    styles.fullWidth,
                    styles.doorsBrandPopup,
                    !isWeb ? styles.fullWidthMobile : {},
                  ]}>
                  {dataDoorsList.map((item, idx) => (
                    <ProgressChart
                      title={item?.brand?.name || '-'}
                      target={convertTargetSettingValue(
                        item.total_target_value || 0,
                        item.total_target_type || 'doors',
                      )}
                      count={item?.percentage || 0}
                      popupText={`${today_month_name}: ${convertTargetSettingValue(
                        item.total_actual_value || 0,
                        item.total_target_type || 'doors',
                        false,
                      )}`}
                      showPopup
                      titleRowMobile
                      leftSpace
                      key={idx}
                    />
                  ))}
                </View>
              )}
            </>
          ) : (
            <View style={styles.noData}>
              <SociollaText>
                No data found. Please use another filter!
              </SociollaText>
            </View>
          )}
        </ScrollView>
      </ModalFull>
    </View>
  );
};

const styles = StyleSheet.create({
  fullWidth: { width: isWeb ? '100%' : widthMobile },
  fullWidthNormal: { width: '100%' },
  fullWidthMobile: { width: fullWidth },
  rowBetween: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  rowWrap: { flexWrap: 'wrap' },
  rowColumn: { flexDirection: 'column' },
  leftSide: {
    width: widthLeft,
  },
  rightSide: {
    width: widthRight,
  },
  titleStyle: {
    color: Colors.black0,
    fontSize: 15,
    lineHeight: 22,
  },
  // netOrderTotal: {
  //   flexDirection: 'row',
  //   alignItems: 'center',
  //   justifyContent: 'space-between',
  // },
  // netOrderText: { fontSize: 24, color: Colors.blue0 },
  // netOrderRightTop: { fontSize: 9, color: Colors.grey1, textAlign: 'right' },
  // netOrderRightBottom: {
  //   fontSize: 12,
  //   color: Colors.green1,
  //   textAlign: 'right',
  // },
  // ytdText: { fontSize: 10 },
  // progressNetStyle: { marginTop: 5, marginBottom: 0, paddingBottom: 15 },
  filterNetContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  filterNet: { fontSize: 12, color: Colors.grey1, marginRight: 10 },
  // filterNetOrder: { width: 40 },
  // filterNetBox: { width: fullWidth - 100 },
  filterNetSalesmanType: { width: 110 },
  filterNetBoxSalesmanType: {
    width: isWeb ? 'calc(100% - 110px)' : fullWidth - 175,
  },
  chartWrapper: {
    width: '100%',
    justifyContent: 'center',
    marginTop: 15,
    // width: isWeb ? 'calc(100% + 120px)' : fullWidth,
    // justifyContent: 'center',
    // marginHorizontal: -25,
    // marginBottom: -10,
    // marginTop: -35,
  },
  chartWrapperNormal: {
    width: '100%',
    marginHorizontal: 0,
    marginBottom: 0,
    marginTop: 15,
  },
  pieChartWrapper: {
    width: isWeb ? 360 : '100%',
    // height: 300,
    justifyContent: 'center',
  },
  tabWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    elevation: 1,
    marginTop: -30,
  },
  tabWrapperNormal: { marginTop: 0, justifyContent: 'flex-start' },
  tabWrapperMobile: {
    width: '100%',
    marginTop: 0,
    justifyContent: 'space-between',
  },
  tabWrapperFull: { width: '100%' },
  tabItem: {
    marginHorizontal: isWeb ? 10 : 0,
    paddingBottom: 5,
    paddingHorizontal: 5,
    borderBottomColor: Colors.transparent,
    borderBottomWidth: 3,
  },
  tabItemActive: { borderBottomColor: Colors.blue0 },
  filterChartWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 10,
    zIndex: 1,
    elevation: 1,
  },
  filterChartItem: { flexDirection: 'row', alignItems: 'center' },
  labelCatChild: { flexDirection: 'row', marginBottom: 10 },
  labelCatChild2: { flexDirection: 'column' },
  labelCatBox: { width: 12, height: 12, marginRight: 10 },
  labelCatText: { fontSize: 10 },
  labelCatBigText: { fontSize: 24 },
  labelCatBigText2: { fontSize: 18 },
  pieCat: isWeb
    ? {
        paddingTop: 40,
        width: 250,
      }
    : {
        paddingTop: 30,
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      },
  pieCatItem: !isWeb
    ? {
        maxWidth: '50%',
      }
    : {},
  minBottom: { marginBottom: 10 },
  minRight: { marginRight: 10 },
  tabDoors: { width: '100%', flexDirection: 'row' },
  tabDoorsItem: {
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: 3,
    borderBottomColor: Colors.transparent,
    ...(!isWeb
      ? {
          width: '50%',
          justifyContent: 'center',
        }
      : {}),
  },
  tabDoorsItemActive: { borderBottomColor: Colors.blue0 },
  tabDoorsText: { fontSize: 14, color: Colors.grey1, textAlign: 'center' },
  tabDoorsTextActive: { color: Colors.blue0 },
  branchFilterMobile: { width: '100%' },
  pieMobile: { flexDirection: 'column' },
  filterPie: { flexWrap: 'wrap' },
  filterPieBox: { width: '100%', marginTop: 10 },
  productWrapper: { paddingHorizontal: 15, marginBottom: 10 },
  titleProgressBar: isWeb ? { fontSize: 28 } : { fontSize: 20, marginTop: 5 },
  productItem: { paddingHorizontal: 0, paddingTop: 10, paddingBottom: 0 },
  progressProduct: {
    marginTop: 5,
    marginBottom: 15,
    paddingBottom: 15,
  },
  tabChart: {
    width: 100,
    marginHorizontal: 'auto',
  },
  categoryStyle: {
    position: 'relative',
    marginTop: 15,
  },
  filterNetContainerSalesmanType: {
    zIndex: 99,
  },
  noData: {
    padding: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 80,
  },
  doorsBrandPopup: {
    paddingHorizontal: 15,
  },
});

BusinessTracker.propTypes = propTypes;
BusinessTracker.defaultProps = defaultProps;

export default React.memo(BusinessTracker, (prev, next) => prev == next);
